// ============================================================
// SquadThread - the controller's thread to its units, on the display
// (plan map-display, Track E; the session study).
//
// While the actor is one of a control network (engine/roster/control), a
// dashed thread runs from the controller's foot to each unit's, bowed a
// little below the straight line, its dashes marching away from the
// controller. Drawn under the figures; it takes no pointer. A network with
// no controller here, or one whose members have no markers, draws nothing.
// ============================================================
import { memo } from 'react';
import { SCENE_VIEW, project, type Camera } from '../../engine/battlefield/projection';
import { tokenFoot, type Token } from '../../engine/battlefield/tokens';
import { networkOfActor } from '../../engine/roster/control';
import type { Combatant } from '../../engine/roster/types';
import styles from '../../styles/Display.module.css';

/** How far below the straight line the thread hangs, in screen units. */
const SAG = 18;

/** A foot on the ground, on the screen. */
const footOf = (t: Token, camera: Camera) => project(tokenFoot(t), camera, SCENE_VIEW);

function SquadThread({
  roster,
  tokens,
  actorId,
  camera,
}: {
  roster: readonly Combatant[];
  tokens: Readonly<Record<string, Token>>;
  actorId: string | null;
  camera: Camera;
}) {
  const network = networkOfActor(roster, actorId);
  const from = network?.controller ? tokens[network.controller.id] : undefined;
  if (!network || !from) return null;
  const a = footOf(from, camera);
  // A foot behind the eye (perspective) projects nowhere sensible: no thread to or from it.
  const units = a.behind
    ? []
    : network.members
        .filter((m) => m.id !== network.controller?.id && tokens[m.id])
        .map((m) => ({ id: m.id, b: footOf(tokens[m.id], camera) }))
        .filter((u) => !u.b.behind);
  return (
    <g data-testid="squad-thread" pointerEvents="none">
      {units.map(({ id, b }) => {
        const cx = (a.x + b.x) / 2;
        const cy = (a.y + b.y) / 2 + SAG;
        return (
          <path
            key={id}
            className={styles.squadThread}
            data-unit={id}
            d={`M${a.x.toFixed(1)} ${a.y.toFixed(1)} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}`}
          />
        );
      })}
    </g>
  );
}

export default memo(SquadThread);
